import { useState, useEffect } from 'react';
import type { Group, GroupInput } from '../../api';

interface GroupFormProps {
  group?: Group | null;
  parentGroups: Group[];
  onSubmit: (data: GroupInput) => Promise<void> | void;
  onCancel: () => void;
}

export default function GroupForm({ group, parentGroups, onSubmit, onCancel }: GroupFormProps) {
  const [form, setForm] = useState<GroupInput>({ name: '', description: '', parent_id: null });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (group) {
      setForm({ name: group.name || '', description: group.description || '', parent_id: group.parent_id || null });
    }
  }, [group]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit(form);
    } finally {
      setLoading(false);
    }
  }

  const inputClass =
    'mt-1.5 w-full rounded-lg border border-border bg-input px-3 py-2 text-[13px] text-foreground placeholder-muted-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-ring';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-[13px] font-medium text-foreground">Name *</label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          required
          className={inputClass}
          placeholder="Group name"
        />
      </div>

      <div>
        <label className="block text-[13px] font-medium text-foreground">Description</label>
        <textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          rows={3}
          className={inputClass}
          placeholder="Optional description"
        />
      </div>

      <div>
        <label className="block text-[13px] font-medium text-foreground">Parent Group</label>
        <select
          value={form.parent_id || ''}
          onChange={(e) => setForm({ ...form, parent_id: e.target.value || null })}
          className={inputClass}
        >
          <option value="">None (root level)</option>
          {(parentGroups || [])
            .filter((g) => !group || g.id !== group.id)
            .map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
        </select>
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="rounded-lg px-4 py-2 text-[13px] font-medium text-muted-foreground hover:bg-muted transition-colors">
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading || !form.name}
          className="rounded-lg bg-brand px-4 py-2 text-[13px] font-semibold text-white hover:bg-brand-dark disabled:opacity-50 transition-colors"
        >
          {loading ? 'Saving…' : group ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
}
